import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { BSON } from 'realm-web';

import { getCollection } from '@/utils';
import { CATEGORIES_COLLECTION, PRODUCTS_COLLECTION } from '@/constants/constants';
import { Platform } from '@/enums/platform';

export interface Category {
  _id: string;
  name: string;
  description?: string;
  parentId?: string;
  companyId: string;
  externalId?: string;
  image?: string;
  menuOrder?: number;
  source?: Platform;
}

type CategoryInput = Omit<Category, '_id'> & { _id?: string };

export const categoriesApiSlice = createApi({
  reducerPath: 'categoriesApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['CategoriesApi'],
  endpoints: (builder) => ({
    fetchCategories: builder.query<Category[], { companyId: string }>({
      async queryFn({ companyId }) {
        try {
          const categoriesColl = getCollection(CATEGORIES_COLLECTION);

          const categories = await categoriesColl?.aggregate([
            { $match: { companyId: new BSON.ObjectID(companyId) } },
          ]);

          return { data: BSON.EJSON.deserialize(categories) as Category[] };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
      providesTags: [{ type: 'CategoriesApi' }],
    }),
    createOrUpdateCategory: builder.mutation<Category, CategoryInput>({
      async queryFn(categoryInput) {
        const { _id, externalId, companyId, parentId, ...rest } = categoryInput;

        const newId = _id ? new BSON.ObjectID(_id) : new BSON.ObjectID();
        const categoriesColl = getCollection(CATEGORIES_COLLECTION);
        try {
          const newCategory = await categoriesColl?.findOneAndUpdate(
            { _id: newId },
            {
              $set: {
                ...rest,
                externalId,
                companyId: new BSON.ObjectID(companyId),
                ...(parentId ? { parentId: new BSON.ObjectID(parentId) } : {}),
                source: Platform.STAND_ALONE,
              },
            },
            { upsert: true, returnNewDocument: true },
          );
          return { data: BSON.EJSON.deserialize(newCategory) as Category };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
      invalidatesTags: [{ type: 'CategoriesApi' }],
    }),
    deleteCategory: builder.mutation<string[], string[]>({
      async queryFn(categoryIds) {
        try {
          const categoryCollection = getCollection(CATEGORIES_COLLECTION);

          const productCollection = getCollection(PRODUCTS_COLLECTION);

          const objectIdCategoryIds = categoryIds.map((id) => new BSON.ObjectID(id));

          await categoryCollection?.deleteMany({
            _id: { $in: objectIdCategoryIds },
          });

          await productCollection?.updateMany(
            {
              categories: { $in: objectIdCategoryIds },
              source: Platform.STAND_ALONE,
            },
            { $pull: { categories: { $in: objectIdCategoryIds } } },
          );

          return {
            data: categoryIds,
          };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
      invalidatesTags: [{ type: 'CategoriesApi' }],
    }),
    updateCategoryOrder: builder.mutation<Category[], Category[]>({
      async queryFn(updatedCategories) {
        const categoriesColl = getCollection(CATEGORIES_COLLECTION);
        try {
          const updatedCategoriesList = await Promise.all(
            updatedCategories.map(async (category) => {
              const updatedCategory = await categoriesColl?.findOneAndUpdate(
                { _id: new BSON.ObjectID(category._id) },
                { $set: { menuOrder: category.menuOrder } },
                { returnNewDocument: true },
              );
              return BSON.EJSON.deserialize(updatedCategory) as Category;
            }),
          );
          return { data: updatedCategoriesList };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
      invalidatesTags: [{ type: 'CategoriesApi' }],
    }),
    updateCategory: builder.mutation<Category, Partial<Category>>({
      async queryFn(category) {
        const { _id, ...rest } = category;

        const categoriesColl = getCollection(CATEGORIES_COLLECTION);
        try {
          const updatedCategory = await categoriesColl?.findOneAndUpdate(
            { _id: new BSON.ObjectID(_id) },
            { $set: rest },
            { returnNewDocument: true },
          );
          return { data: BSON.EJSON.deserialize(updatedCategory) as Category };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
      invalidatesTags: [{ type: 'CategoriesApi' }],
    }),
    fetchCategoriesWithNested: builder.query<any, { companyId: string }>({
      async queryFn({ companyId }) {
        try {
          const categoriesCollection = getCollection('categories');
          const categories = await categoriesCollection?.aggregate([
            {
              $match: {
                companyId: new BSON.ObjectID(companyId),
                parentId: null,
              },
            },
            {
              $addFields: {
                id: '$_id',
                label: '$name',
                value: '$_id',
              },
            },
            {
              $lookup: {
                from: 'categories',
                localField: '_id',
                foreignField: 'parentId',
                as: 'nestedOption',
              },
            },
            {
              $addFields: {
                nestedOption: {
                  $map: {
                    input: '$nestedOption',
                    as: 'subCategory',
                    in: {
                      id: '$$subCategory._id',
                      label: '$$subCategory.name',
                    },
                  },
                },
              },
            },
          ]);

          if (!categories) return { data: [] };

          return { data: BSON.EJSON.deserialize(categories) };
        } catch (error: any) {
          return { error: error.message || 'Unknown error' };
        }
      },
      providesTags: ['CategoriesApi'],
    }),
  }),
});

export const {
  useFetchCategoriesQuery,
  useCreateOrUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryOrderMutation,
  useUpdateCategoryMutation,
  useFetchCategoriesWithNestedQuery,
} = categoriesApiSlice;
